// The internal service API (spec/08 §5; spec/09 §5; INFRA-18, #379).
//
// Service to service, under `/internal/v1/*`, never exposed at the edge, authenticated by a service
// JWT (mTLS underneath is the network's). Four endpoints:
//
//   POST /internal/v1/stations/events    api      the gateway's micro-batches (spec/09 §5.2): each
//                                                 station's sequenced events, the hello it said, the
//                                                 command acks it sent and the commands delivered to
//                                                 it. The answer is each station's `upTo` — the last
//                                                 `seq` durably processed, which the gateway acks —
//                                                 and, for a hello, `station.state` and the commands
//                                                 to send again (§5.3).
//   POST /internal/v1/realtime/publish   gateway  deliver a frame on a channel (spec/08 §5).
//   POST /internal/v1/realtime/access    api      a member's access now — their venues and channel
//                                                 permissions — for the gateway to re-check their
//                                                 subscriptions after `access.changed` (spec/09 §3).
//   GET  /internal/v1/realtime/jwks      api      the public keys realtime tokens are signed with, for
//                                                 the gateway to verify them (spec/09 §1). better-auth's
//                                                 own `/v1/auth/jwks` is switched off: it is outside
//                                                 the contract, and no client verifies a token.
//
// Outside `/v1` on purpose: no client is generated for them and the breaking-change gate does not
// apply (tools/src/contract/breaking.ts). Their shapes are inline rather than named components, for
// the same reason — a named component is emitted into the station's DTOs and the console's client,
// and neither calls these. The station-facing payloads inside them (`p`) travel as objects and are
// validated against their own schemas on each side.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { BootServerIdSchema, DeviceIdSchema, MemberIdSchema, OrganizationIdSchema, StationCommandIdSchema, StationIdSchema, VenueIdSchema, } from "../ids/schema.js";
import { jsonResponse, problemResponse } from "../openapi/responses.js";
import { IsoDateTimeSchema, PermissionSchema } from "../primitives/scalars.js";
import { CHANNEL_PATTERN } from "../realtime/channel.js";
import { REALTIME_FRAME_ID_PATTERN } from "../realtime/envelope.js";
import { StationStatusPayloadSchema } from "../realtime/station.js";
// Inline, not the named `RealtimeFrameId` and `RealtimeChannel` components: see the header.
const FrameIdSchema = z.string().regex(REALTIME_FRAME_ID_PATTERN);
const InternalChannelSchema = z.string().regex(CHANNEL_PATTERN);
const SERVICE_AUTH = 'Service-to-service only: `Authorization: Bearer` with a service JWT. Anything else is `404`, so from ' +
    'outside the private network the internal API is indistinguishable from nothing.';
/** How many stations and frames one batch may carry. The gateway sends 50 events per flush. */
export const INTERNAL_BATCH_LIMITS = { stations: 500, framesPerStation: 1000 };
// ---------------------------------------------------------------------------------------------
// POST /internal/v1/stations/events
// ---------------------------------------------------------------------------------------------
const StationFrameSchema = z.object({
    id: FrameIdSchema,
    type: z.string().min(1).max(128),
    ts: IsoDateTimeSchema,
    p: z.looseObject({}),
});
export const StationEventBatchSchema = z.object({
    stations: z
        .array(z.object({
        stationId: StationIdSchema,
        /** From the station's verified realtime token, which is where the gateway learned it. */
        organizationId: OrganizationIdSchema,
        venueId: VenueIdSchema,
        /** The `station.hello` it sent since the last batch, if it sent one (spec/09 §5.3). */
        hello: StationFrameSchema.optional(),
        /**
         * Sequenced events, in the order received. The api records those above its last `seq` **in order,
         * with no gap**: an event above a missing `seq` is held, not recorded, and the cursor stays below the
         * gap — so the station keeps the missing one and sends it again (spec/09 §5.2, 2026-10-01).
         */
        events: z.array(StationFrameSchema.extend({ seq: z.int().min(1) })).max(INTERNAL_BATCH_LIMITS.framesPerStation),
        /**
         * Sequenced frames the gateway refused (`err invalid-frame`, or a type it does not know) but whose `seq`
         * it could read (2026-10-01, INFRA-14's finding). The station will send each again for ever and never
         * get it past the gateway, so the api records it as a dead letter (`stations.station_event.rejected`,
         * with what the station sent) and moves the cursor over it — explicitly, never by skipping the gap.
         * Absent from an older gateway.
         */
        rejected: z
            .array(z.object({
            seq: z.int().min(1),
            /** The frame's `id`, when it had a readable one. */
            id: z.string().max(128).nullable(),
            /** The frame's `type` as sent, when it had one — possibly the malformed thing that got it refused. */
            type: z.string().max(128).nullable(),
            /** `invalid-frame`: not a valid envelope or not valid for its kind; `unknown-type`: not a station event type. */
            code: z.enum(['invalid-frame', 'unknown-type']),
            /** Why, in words, for the dead letter. */
            reason: z.string().max(500),
            /** When the gateway refused it. */
            at: IsoDateTimeSchema,
            /** The frame's payload, when it had an object one: the dead letter keeps what the station reported. */
            p: z.looseObject({}).optional(),
        }))
            .max(INTERNAL_BATCH_LIMITS.framesPerStation)
            .optional(),
        /** Command acks (spec/09 §5.1): `re` is the command, `p` is `{ ok, result }` or `{ ok, error }`. */
        acks: z
            .array(z.object({ commandId: StationCommandIdSchema, at: IsoDateTimeSchema, p: z.looseObject({}) }))
            .max(INTERNAL_BATCH_LIMITS.framesPerStation),
        /** Commands written to the station's socket: `queued` becomes `sent`. */
        delivered: z.array(StationCommandIdSchema).max(INTERNAL_BATCH_LIMITS.framesPerStation),
        /**
         * One telemetry sample — the station's latest `station.status`, kept by the gateway **at most once a
         * minute** per station (F-STN-11, spec/09 §8): a `station_telemetry` row, never a row per report.
         */
        telemetry: z.object({ at: IsoDateTimeSchema, status: StationStatusPayloadSchema }).optional(),
    }))
        .min(1)
        .max(INTERNAL_BATCH_LIMITS.stations),
});
export const StationEventBatchResultSchema = z.object({
    stations: z.array(z.object({
        stationId: StationIdSchema,
        /** The last `seq` durably processed for this station — what the gateway acknowledges. */
        upTo: z.int().min(0),
        /** For a batch with a hello: `station.state`'s payload (spec/09 §5.3). */
        state: z.looseObject({}).optional(),
        /** For a batch with a hello: the unexpired, unacknowledged, non-session commands to send again. */
        commands: z
            .array(z.object({
            id: StationCommandIdSchema,
            type: z.string().min(1),
            payload: z.looseObject({}),
            createdAt: IsoDateTimeSchema,
        }))
            .optional(),
    })),
});
export const ingestStationEventsRoute = createRoute({
    method: 'post',
    path: '/internal/v1/stations/events',
    operationId: 'ingestStationEvents',
    tags: ['internal'],
    summary: 'The realtime gateway forwards station events, hellos and command acks (micro-batched)',
    description: `${SERVICE_AUTH} Each station's events are processed at most once: those at or below its last processed ` +
        '`seq` are ignored, and the rest are recorded in the transaction that advances it (spec/09 §5.2).',
    request: { body: { content: { 'application/json': { schema: StationEventBatchSchema } }, required: true } },
    responses: {
        200: jsonResponse(StationEventBatchResultSchema, "Each station's last processed seq, and its state after a hello."),
        400: problemResponse('The batch could not be understood.', 'validation-failed'),
        404: problemResponse('Not a service caller.', 'not-found'),
        500: problemResponse('Something went wrong; the gateway acknowledges nothing and the station replays.', 'internal-error'),
        503: problemResponse('Busy: the api is already recording as many batches as it takes at once, or has no database connection for this ' +
            'one. Nothing in it was recorded. The gateway keeps the batch and sends it again after `Retry-After` (whole ' +
            'seconds), with its own jitter, and sends fewer batches at once until the api answers again (spec/09 §5.2).', 'service-unavailable'),
    },
});
// ---------------------------------------------------------------------------------------------
// POST /internal/v1/realtime/publish
// ---------------------------------------------------------------------------------------------
export const RealtimePublishRequestSchema = z.object({
    channel: InternalChannelSchema,
    /** A realtime envelope (spec/09 §2); the gateway validates it before it goes anywhere. */
    frame: z.looseObject({}),
});
export const publishRealtimeFrameRoute = createRoute({
    method: 'post',
    path: '/internal/v1/realtime/publish',
    operationId: 'publishRealtimeFrame',
    tags: ['internal'],
    summary: 'Deliver a frame on a channel, through whichever gateway node holds its sockets',
    description: `${SERVICE_AUTH} Served by the realtime gateway. Best effort, like all realtime delivery: what must survive is a row.`,
    request: { body: { content: { 'application/json': { schema: RealtimePublishRequestSchema } }, required: true } },
    responses: {
        200: jsonResponse(z.object({ nodes: z.int().min(0) }), 'How many gateway nodes were listening on the channel.'),
        400: problemResponse('The frame could not be understood.', 'validation-failed'),
        404: problemResponse('Not a service caller.', 'not-found'),
    },
});
// ---------------------------------------------------------------------------------------------
// POST /internal/v1/realtime/access
// ---------------------------------------------------------------------------------------------
export const RealtimeAccessRequestSchema = z.object({
    organizationId: OrganizationIdSchema,
    memberId: MemberIdSchema,
});
export const RealtimeAccessResultSchema = z.object({
    /** `false` when the membership is gone or suspended: the gateway closes the socket with `4403`. */
    active: z.boolean(),
    /** The venues the member works at — every live venue of the organisation for one with no venue scope. */
    venues: z.array(VenueIdSchema),
    /** The member's permissions that gate a channel (spec/09 §3). */
    permissions: z.array(PermissionSchema),
    /** The access hash a realtime token minted now would carry (its `perm` claim). */
    perm: z.string(),
});
export const resolveRealtimeAccessRoute = createRoute({
    method: 'post',
    path: '/internal/v1/realtime/access',
    operationId: 'resolveRealtimeAccess',
    tags: ['internal'],
    summary: "A member's realtime access now: the venues they work at and the channel permissions they hold",
    description: `${SERVICE_AUTH} Read fresh from the database, by the function every permission check calls (spec/07 §6.3).`,
    request: { body: { content: { 'application/json': { schema: RealtimeAccessRequestSchema } }, required: true } },
    responses: {
        200: jsonResponse(RealtimeAccessResultSchema, 'The access, now.'),
        400: problemResponse('The request could not be understood.', 'validation-failed'),
        404: problemResponse('Not a service caller.', 'not-found'),
    },
});
// ---------------------------------------------------------------------------------------------
// GET /internal/v1/realtime/jwks
// ---------------------------------------------------------------------------------------------
/** One public key, as better-auth's `jwt` plugin publishes it: Ed25519, so `OKP` with `crv` and `x`. */
export const RealtimePublicKeySchema = z
    .object({
    kid: z.string().optional(),
    kty: z.string(),
    crv: z.string().optional(),
    x: z.string().optional(),
    alg: z.string().optional(),
});
export const RealtimeJwksSchema = z.object({ keys: z.array(RealtimePublicKeySchema) });
export const getRealtimeJwksRoute = createRoute({
    method: 'get',
    path: '/internal/v1/realtime/jwks',
    operationId: 'getRealtimeJwks',
    tags: ['internal'],
    summary: 'The public keys realtime tokens are signed with',
    description: `${SERVICE_AUTH} The keys of the api's JWKS, which signs every realtime token (spec/09 §1). The gateway ` +
        'refreshes them when a token names a key it has not seen, so a rotation reaches it without a restart.',
    responses: {
        200: jsonResponse(RealtimeJwksSchema, 'The keys.'),
        404: problemResponse('Not a service caller.', 'not-found'),
    },
});
// ---------------------------------------------------------------------------------------------
// POST /internal/v1/boot-servers/events
// ---------------------------------------------------------------------------------------------
/** How many events one boot-server batch may carry. The gateway forwards each frame as it arrives. */
export const BOOT_SERVER_BATCH_LIMIT = 100;
export const BootServerEventBatchSchema = z.object({
    /** From the boot server's verified realtime token, which is where the gateway learned them. */
    bootServerId: BootServerIdSchema,
    /** The token's `sub`: the device the server proved itself with. The api refuses a revoked one. */
    deviceId: DeviceIdSchema,
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    /**
     * `boot.server.status`, `boot.pc.booted`, `boot.metrics` or `boot.job.progress` (spec/25 §13.5), each already
     * validated against its payload schema by the gateway and validated again here.
     */
    events: z
        .array(z.object({ id: FrameIdSchema, type: z.string().min(1).max(128), ts: IsoDateTimeSchema, p: z.looseObject({}) }))
        .min(1)
        .max(BOOT_SERVER_BATCH_LIMIT),
});
export const BootServerEventBatchResultSchema = z.object({
    /** `recorded`: in the outbox, for the `boot` module's handlers. `revoked`: the device is no longer this server's. */
    status: z.enum(['recorded', 'revoked']),
    recorded: z.int().min(0),
});
export const ingestBootServerEventsRoute = createRoute({
    method: 'post',
    path: '/internal/v1/boot-servers/events',
    operationId: 'ingestBootServerEvents',
    tags: ['internal'],
    summary: 'The realtime gateway forwards a Clavian Boot server’s events',
    description: `${SERVICE_AUTH} Each event is written to the outbox in one transaction, keyed by its frame id, and the \`boot\` ` +
        'module’s handlers record it (spec/25 §13.5). A device that is revoked or no longer bound to the server answers ' +
        '`revoked`, and the gateway closes the socket with `4403`.',
    request: { body: { content: { 'application/json': { schema: BootServerEventBatchSchema } }, required: true } },
    responses: {
        200: jsonResponse(BootServerEventBatchResultSchema, 'Recorded, or refused because the device is revoked.'),
        400: problemResponse('The batch could not be understood.', 'validation-failed'),
        404: problemResponse('Not a service caller.', 'not-found'),
        500: problemResponse('Something went wrong; the gateway answers the boot server `unavailable` and it sends again.', 'internal-error'),
    },
});
export const INTERNAL_ROUTES = [
    ingestStationEventsRoute,
    ingestBootServerEventsRoute,
    publishRealtimeFrameRoute,
    resolveRealtimeAccessRoute,
    getRealtimeJwksRoute,
];
//# sourceMappingURL=internal.js.map